import React, { useState } from "react";
import {
  Alert,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CashPaymentMethod } from "../methods/cash";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types/rootState";
import { TransferPaymentMethod } from "../methods/transfer";
import BankAccounts from "../../raffle/bankAccounts";
import PromotionSteps from "../../raffle/promotionSteps";
import { BankAccountItem } from "../../../constants/bankAccounts";
import { hasSelectableNumbers, isTalonario } from "../../../utils/raffleType";

interface FormPaymentProps {
  setPurchaserData: (data: any) => void;
  handlePaymentMethodSelect: (paymentMethod: string) => void;
  setOpen: (open: boolean) => void;
  availablePaymentMethods: string[];
  selectedTickets: number[] | undefined;
  purchaserData: any;
  selectedPaymentMethod: string | null;
  raffleId: string;
  ticketQuantity: number;
  totalAmount: number;
  totalAmountUSD: number;
  minPurchase: number;
}

const FormPayment: React.FC<FormPaymentProps> = ({
  purchaserData,
  setPurchaserData,
  availablePaymentMethods,
  selectedPaymentMethod,
  handlePaymentMethodSelect,
  selectedTickets,
  setOpen,
  raffleId,
  ticketQuantity,
  totalAmount,
  totalAmountUSD,
  minPurchase,
}: FormPaymentProps) => {
  const isLoggedIn = useSelector(({ authentication }: RootState) => authentication.isLoggedIn);
  const accountId = useSelector(({ account }: RootState) => account.id);
  const raffleType = useSelector(({ raffle }: RootState) => raffle.type);
  const hasReferenceNumber = useSelector(({ raffle }: RootState) => raffle.hasReferenceNumber);
  const [emailError, setEmailError] = useState<string>("");
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountItem | undefined>();

  const validateEmail = (email: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let email = e.target.value;
    email = email.trim().toLowerCase();
    setPurchaserData({ ...purchaserData, email });
    if (!validateEmail(email)) {
      setEmailError("Ingrese un correo electrónico válido");
    } else {
      setEmailError("");
    }
  };


  const renderPaymentOption = () => {
    switch (selectedPaymentMethod) {
        case "cash":
          return (
            <CashPaymentMethod
              totalAmount={totalAmount}
              purchaserData={purchaserData}
              raffleId={raffleId}
              setOpen={setOpen}
              selectedPaymentMethod={selectedPaymentMethod}
              ticketQuantity={ticketQuantity}
            />
          );
        case "transfer":
            return (
              <TransferPaymentMethod
                selectedTickets={selectedTickets}
                minPurchase={minPurchase}
                totalAmount={totalAmount}
                totalUSDAmount={totalAmountUSD}
                purchaserData={purchaserData}
                raffleId={raffleId}
                setOpen={setOpen}
                selectedPaymentMethod={selectedPaymentMethod}
                ticketQuantity={ticketQuantity}
                selectedBankAccount={selectedBankAccount}
              />
            );
      default:
        return null;
    }
  };

  return (
    <form style={{
      height: '100%',
      width: '100%',
    }}>

      {(raffleType === 'raffle' || hasSelectableNumbers(raffleType)) && <BankAccounts ticketSelectedCount={ticketQuantity}  accountId={accountId} raffleId={raffleId} setSelectedBankAccount={setSelectedBankAccount}></BankAccounts> }
      {raffleType === 'promo' && <PromotionSteps accountId={accountId}></PromotionSteps> }
      {raffleType === 'raffle' && <Typography variant="h6" textAlign={"center"} my={2} fontWeight={"bold"}>¿Ya transferiste? <br/> Llena este formulario:</Typography>}
      {isTalonario(raffleType) && <Box my={2} textAlign={"center"}>
        <Typography variant="h6" fontWeight={"bold"}>Aparta tus números</Typography>
        <Typography variant="body2">Puedes adjuntar el comprobante ahora o después desde tu link de tickets (será necesario para validar tu compra).</Typography>
      </Box>}
      <TextField
        sx={{ paddingBottom: 2 }}
        id="name"
        label={raffleType === 'promo' ? "Nombre de usuario de instagram" : "Nombre del comprador"}
        type="text"
        fullWidth
        variant="outlined"
        required
        value={purchaserData.name}
        inputProps={{
          maxLength: 50
        }}
        onChange={(e) =>
          setPurchaserData({ ...purchaserData, name: e.target.value })
        }
      />
      {raffleType !== 'promo' && <TextField
        sx={{ paddingBottom: 2 }}
        id="email"
        label="Email"
        type="email"
        fullWidth
        variant="outlined"
        required
        error={!!emailError}
        inputProps={{
          maxLength: 50
        }}
        helperText={emailError}
        value={purchaserData.email}
        onChange={handleEmailChange}
      />}
      
        <TextField
        sx={{ paddingBottom: 2 }}
        margin="dense"
        id="phone"
        label="Número de teléfono"
        type="text"
        fullWidth
        variant="outlined"
        required
        value={purchaserData.phone}
        inputProps={{
          maxLength: 20,
          pattern: "[0-9]*"
        }}
        onInput={(e: any) => {
          e.target.value = e.target.value.replace(/[^0-9+]/g, ''); // Filter out non-numeric characters
          if (raffleType === 'promo') {
            setPurchaserData({ ...purchaserData, email: e.target.value, phone: e.target.value });
          } else {
            setPurchaserData({ ...purchaserData, phone: e.target.value });
          }
        }}
      />

      {
              hasReferenceNumber && <Box sx={{ paddingBottom: 2 }}>
                <TextField
                  margin="dense"
                  id="referenceNumber"
                  label={isTalonario(raffleType) ? "Número de referencia (opcional)" : "Número de referencia"}
                  type="text"
                  fullWidth
                  variant="outlined"
                  required={!isTalonario(raffleType)}
                  value={purchaserData.referenceNumber}
                  InputProps={{
                    inputProps: {
                      maxLength: accountId === 'super-rifas-latam' ? 6 : 15,
                    }
                  }}
                  onInput={(e: any) => {
                    e.target.value = e.target.value.replace(/\s+/g, '');
                    setPurchaserData({ ...purchaserData, referenceNumber: e.target.value });
                  }}
                />
                <Alert severity="warning" sx={{ fontSize: 12 }}>Número de referencia que no coincida con el comprobante será rechazado. { accountId === 'super-rifas-latam' ? 'Recuerde poner los últimos 6 digitos.' : 'Debe agregar la referencia completa.' }</Alert>
              </Box> 
              
            }
      
      <TextField
        sx={{ paddingBottom: 2 }}
        margin="dense"
        id="phone"
        label="Número de tickets"
        disabled
        type="text"
        fullWidth
        variant="outlined"
        required
        value={ticketQuantity}
        inputProps={{
          maxLength: 12,
          pattern: "[0-9]*"
        }}
        onInput={(e: any) => {
          e.target.value = e.target.value.replace(/[^0-9+]/g, ''); // Filter out non-numeric characters
          setPurchaserData({ ...purchaserData, phone: e.target.value });
        }}
      />
      {renderPaymentOption()}
    </form>
  );
};

export default FormPayment;
